import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';

/** Clave para persistir el iframe abierto y restaurarlo tras refrescar la página. */
const PLAYER_IFRAME_KEY = 'player.iframeUrl';

/** Estructura persistida: la URL del player + la ruta del curso donde se abrió. */
interface PersistedPlayer {
  url: string;
  path: string;
}

@Injectable({
  providedIn: 'root',
})
export class PlayerService {
  // Ruta (/learn/{id}/...) donde se abrió el iframe actualmente visible.
  private openedRoutePath: string | null = null;

  // Inicializa desde localStorage SOLO si la app arrancó/refrescó en la MISMA URL del
  // curso donde se abrió el player. En cualquier otra ruta (login/company, otro curso,
  // etc.) el iframe no se restaura.
  private iframeUrl = new BehaviorSubject<string | null>(this.getRestorableIframeUrl());
  iframeUrl$ = this.iframeUrl.asObservable();

  constructor(private router: Router) {
    // Si quedó un iframe restaurado (refresco en su URL específica), restaura el bloqueo de scroll.
    if (this.iframeUrl.value) {
      document.body.style.setProperty('overflow', 'hidden', 'important');
    }

    // El iframe-container solo debe existir en la URL del curso donde se abrió. Si la
    // navegación termina (o un guard redirige) a una ruta distinta —p. ej. refrescar y
    // caer en login/company, u otra ruta /learn/ de otro curso— cerramos el iframe.
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        if (this.iframeUrl.value && !this.samePath(this.openedRoutePath, event.urlAfterRedirects)) {
          this.closeIframe();
        }
      });

    window.addEventListener('message', (event) => {
      if (event.data === 'closeIframePlayer') {
        this.closeIframe();
      }
    });
  }

  openIframe(url: string): void {
    // Persiste la URL del player junto con la ruta actual del curso, para poder
    // restaurar el iframe solo si se refresca en esa misma URL.
    this.openedRoutePath = window.location.pathname;
    const persisted: PersistedPlayer = { url, path: this.openedRoutePath };
    localStorage.setItem(PLAYER_IFRAME_KEY, JSON.stringify(persisted));
    document.body.style.setProperty('overflow', 'hidden', 'important');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.iframeUrl.next(url);
  }

  closeIframe(): void {
    // Limpia la persistencia para que el iframe no reaparezca tras un refresco al cerrarlo.
    localStorage.removeItem(PLAYER_IFRAME_KEY);
    this.openedRoutePath = null;
    document.body.style.removeProperty('overflow');
    this.iframeUrl.next(null);
  }

  /**
   * Devuelve la URL del iframe persistida SOLO si (a) el arranque actual es una recarga
   * real (F5) de la página —único escenario para el que se diseñó la restauración— Y
   * (b) esa recarga ocurrió en la MISMA ruta del curso donde se abrió el player. Una
   * entrada nueva a la plataforma (pestaña nueva, navegación directa, back/forward) o
   * un refresco en otra URL descartan la persistencia, para que el overlay del player
   * no reaparezca tapando el navbar ni sobre el curso equivocado.
   */
  private getRestorableIframeUrl(): string | null {
    const [navEntry] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    const persisted = this.readPersisted();

    if (navEntry?.type === 'reload' && persisted && this.samePath(persisted.path, window.location.pathname)) {
      this.openedRoutePath = persisted.path;
      return persisted.url;
    }

    if (localStorage.getItem(PLAYER_IFRAME_KEY)) {
      localStorage.removeItem(PLAYER_IFRAME_KEY);
    }
    return null;
  }

  /** Lee y valida el dato persistido; ignora formatos antiguos (string sin ruta). */
  private readPersisted(): PersistedPlayer | null {
    const raw = localStorage.getItem(PLAYER_IFRAME_KEY);
    if (!raw) {
      return null;
    }
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.url === 'string' && typeof parsed.path === 'string') {
        return parsed as PersistedPlayer;
      }
    } catch {
      // Formato antiguo (solo string): no tiene ruta asociada, no se puede restaurar.
    }
    return null;
  }

  /** Compara dos rutas ignorando query string, fragmento y barra final. */
  private samePath(a: string | null, b: string | null): boolean {
    const norm = (u: string | null) => (u || '').split('#')[0].split('?')[0].replace(/\/+$/, '');
    return norm(a) === norm(b);
  }
}
